import { DatePickerFr } from '../ui/DatePickerFr';
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { CreditCard, Download, CheckCircle, Euro, Building2, Banknote, Loader2, Briefcase, Activity, Wallet } from 'lucide-react';
import { API } from './constants';
import { PaymentLinkSection } from '../PaymentLinkSection';
import { AnnulerPaiementButton } from './AnnulerPaiementButton';
import axios from 'axios';
import { toast } from 'sonner';

export default function ModalsPartialPayment({
  // Validation Modal
  showValidationModal, setShowValidationModal, selectedDocument,
  validationData, setValidationData, validating, handleValidatePayment,
  calculateMontantTTC, calculateMontantHT,
  // Assign Modal
  showAssignModal, setShowAssignModal, assignForm, setAssignForm,
  handleAssign, coopers, members, experts,
  // Share Modal
  showShareModal, setShowShareModal, shareForm, setShareForm,
  sharing, handleShare,
  // Partial Payment Modal
  showPartialPaymentModal, setShowPartialPaymentModal,
  partialPaymentData, setPartialPaymentData,
  partialPaymentsHistory, loadingPartialPayments,
  processingPartialPayment, handlePartialPayment,
  downloadPartialReceipt, archiveRecuToGED: archiveRecuToGED,
  openRecuEcheanceModal: openRecuEcheanceModal, generateRecuEcheance: generateRecuEcheance, generatingRecu: generatingRecu,
  // Echeance Modal
  showEcheanceModal, setShowEcheanceModal,
  echeanceForm, setEcheanceForm, updatingEcheance, handleUpdateEcheance,
  // Echeancier Modal
  showEcheancierModal, setShowEcheancierModal,
  echeancierForm, setEcheancierForm, creatingEcheancier,
  handleCreateEcheancier, addEcheanceToForm, removeEcheanceFromForm,
  // Stripe SEPA Modal
  showStripeSepaModal, setShowStripeSepaModal,
  stripeSepaType, setStripeSepaType, stripeProcessing,
  sepaModalEmail, setSepaModalEmail, sepaModalName, setSepaModalName,
  setupStripeSEPAWithEmail,
  // Edit Acompte Modal
  showEditAcompteModal, setShowEditAcompteModal,
  acompteForm, setAcompteForm, saveAcompteModification,
  // Auth (for embedded PaymentLinkSection inside Partial Payment Modal)
  getAuthHeader = () => ({}),
  // Stripe actions (V37.07 — used by CB / SEPA buttons in Partial Payment Modal)
  initiateStripeCBPayment = () => {},
  openStripeSepaModal = () => {},
  onPaiementAnnule
}) {
  return (
    <>
      <Dialog open={showPartialPaymentModal} onOpenChange={setShowPartialPaymentModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Banknote className="h-5 w-5" />
              Paiement Partiel
            </DialogTitle>
            <DialogDescription>
              Enregistrez un ou plusieurs paiements partiels jusqu'au règlement complet.
            </DialogDescription>
          </DialogHeader>

          {selectedDocument && (
            <div className="space-y-4">
              {/* Informations document */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200">
                <div className="flex items-center gap-3 mb-3">
                  <Building2 className="h-5 w-5 text-orange-500" />
                  <span className="font-bold text-lg">{selectedDocument.raison_sociale}</span>
                </div>
                <p className="text-sm text-gray-600">
                  Réf: {selectedDocument.numero_police || selectedDocument.id}
                </p>
              </div>

              {/* Résumé des montants */}
              {loadingPartialPayments ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                  <span className="ml-2">Chargement...</span>
                </div>
              ) : (
                <>
                  {/* Barre de progression */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50 to-gray-100 border">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-semibold">Progression du paiement</span>
                      <span className="text-orange-600 font-bold">
                        {((partialPaymentsHistory.total_paye || 0) / (partialPaymentsHistory.montant_total_ttc || 1) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-orange-500 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min(100, ((partialPaymentsHistory.total_paye || 0) / (partialPaymentsHistory.montant_total_ttc || 1) * 100))}%` 
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-xs mt-2 text-gray-600">
                      <span>Payé: {(partialPaymentsHistory.total_paye || 0).toFixed(2)} €</span>
                      <span>Total: {(partialPaymentsHistory.montant_total_ttc || 0).toFixed(2)} €</span>
                    </div>
                  </div>

                  {/* Montants récapitulatifs */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                      <p className="text-xs text-blue-600 uppercase">Montant Total</p>
                      <p className="text-lg font-bold text-blue-700">
                        {(partialPaymentsHistory.montant_total_ttc || 0).toFixed(2)} €
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                      <p className="text-xs text-green-600 uppercase">Déjà payé</p>
                      <p className="text-lg font-bold text-green-700">
                        {(partialPaymentsHistory.total_paye || 0).toFixed(2)} €
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-red-50 border-2 border-red-300 text-center">
                      <p className="text-xs text-red-600 uppercase font-bold">Reste à payer</p>
                      <p className="text-lg font-bold text-red-700">
                        {(partialPaymentsHistory.reste_a_payer || 0).toFixed(2)} €
                      </p>
                    </div>
                  </div>

                  {/* Historique des paiements partiels */}
                  {partialPaymentsHistory.paiements && partialPaymentsHistory.paiements.length > 0 && (
                    <div className="border rounded-lg">
                      <div className="bg-gray-100 px-4 py-2 border-b">
                        <h4 className="font-semibold text-sm flex items-center gap-2">
                          <Activity className="h-4 w-4" />
                          Historique des paiements ({partialPaymentsHistory.paiements.length})
                        </h4>
                      </div>
                      <div className="divide-y max-h-40 overflow-y-auto">
                        {partialPaymentsHistory.paiements.map((paiement, index) => (
                          <div key={paiement.id || index} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold text-sm">
                                {paiement.numero || index + 1}
                              </div>
                              <div>
                                <p className="font-semibold text-green-600">
                                  {(paiement.montant || 0).toFixed(2)} €
                                </p>
                                <p className="text-xs text-gray-500">
                                  {paiement.date_paiement} - {paiement.mode_paiement}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadPartialReceipt(selectedDocument.id, paiement.id || paiement.numero)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <AnnulerPaiementButton souscriptionId={selectedDocument.id} paiement={paiement}
                              getAuthHeader={getAuthHeader} onDone={onPaiementAnnule} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Formulaire nouveau paiement partiel */}
                  {(partialPaymentsHistory.reste_a_payer || 0) > 0.01 && (
                    <div className="border-2 border-orange-200 rounded-xl p-4 bg-orange-50/50">
                      <h4 className="font-semibold mb-4 flex items-center gap-2 text-orange-700">
                        <Euro className="h-4 w-4" />
                        Enregistrer un nouveau paiement
                      </h4>
                      
                      {/* Section Paiement en ligne (SEPA) */}
                      <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
                        <h5 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Paiement en ligne (Stripe)
                        </h5>
                        <p className="text-xs text-blue-600 mb-3">
                          Configurez un prélèvement SEPA automatique pour le reste à payer ({(partialPaymentsHistory.reste_a_payer || 0).toFixed(2)} €)
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {/* Bouton CB Stripe */}
                          <Button
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-100"
                            onClick={() => {
                              const amount = partialPaymentsHistory.reste_a_payer || calculateMontantTTC(selectedDocument);
                              initiateStripeCBPayment(selectedDocument, amount);
                            }}
                            disabled={stripeProcessing}
                          >
                            {stripeProcessing ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <CreditCard className="h-4 w-4 mr-2" />
                            )}
                            Carte bancaire
                          </Button>
                          
                          {/* Bouton SEPA Core */}
                          <Button
                            variant="outline"
                            className="border-green-300 text-green-700 hover:bg-green-100"
                            onClick={() => openStripeSepaModal(selectedDocument, 'core')}
                            disabled={stripeProcessing}
                          >
                            {stripeProcessing ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Building2 className="h-4 w-4 mr-2" />
                            )}
                            SEPA Core
                          </Button>
                          
                          {/* Bouton SEPA B2B */}
                          <Button
                            variant="outline"
                            className="border-purple-300 text-purple-700 hover:bg-purple-100"
                            onClick={() => openStripeSepaModal(selectedDocument, 'b2b')}
                            disabled={stripeProcessing}
                          >
                            {stripeProcessing ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Briefcase className="h-4 w-4 mr-2" />
                            )}
                            SEPA B2B
                          </Button>
                        </div>
                        <div className="mt-2 text-xs text-gray-500 space-y-1">
                          <p><span className="font-medium text-green-600">SEPA Core</span> : Particuliers (J-14, remboursable 8 semaines)</p>
                          <p><span className="font-medium text-purple-600">SEPA B2B</span> : Entreprises (J-1, non remboursable)</p>
                        </div>
                      </div>
                      
                      {/* Séparateur */}
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-orange-50 text-gray-500">ou enregistrement manuel</span>
                        </div>
                      </div>
                      
                      {/* Sélection du type de paiement */}
                      <div className="mb-4">
                        <Label className="text-sm font-semibold">Type d'enregistrement</Label>
                        <div className="flex gap-2 mt-2">
                          <Button
                            type="button"
                            variant={partialPaymentData.type_paiement !== 'acompte' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPartialPaymentData({...partialPaymentData, type_paiement: 'partiel'})}
                            className={partialPaymentData.type_paiement !== 'acompte' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                          >
                            <Banknote className="h-4 w-4 mr-2" />
                            Paiement partiel
                          </Button>
                          <Button
                            type="button"
                            variant={partialPaymentData.type_paiement === 'acompte' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPartialPaymentData({...partialPaymentData, type_paiement: 'acompte'})}
                            className={partialPaymentData.type_paiement === 'acompte' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                          >
                            <Wallet className="h-4 w-4 mr-2" />
                            Acompte
                          </Button>
                        </div>
                      </div>

                      {/* Section Acompte spécifique */}
                      {partialPaymentData.type_paiement === 'acompte' && (
                        <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                          <p className="text-xs text-emerald-700 mb-2">
                            <strong>Acompte :</strong> Un acompte est un versement anticipé avant la livraison complète du service. 
                            Il peut être programmé pour encaissement ultérieur.
                          </p>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="is_scheduled"
                              checked={partialPaymentData.is_scheduled || false}
                              onCheckedChange={(checked) => setPartialPaymentData({
                                ...partialPaymentData, 
                                is_scheduled: checked,
                                scheduled_date: checked ? partialPaymentData.scheduled_date : ''
                              })}
                            />
                            <Label htmlFor="is_scheduled" className="text-sm cursor-pointer">
                              Programmer l'encaissement pour une date ultérieure
                            </Label>
                          </div>
                          {partialPaymentData.is_scheduled && (
                            <div className="mt-2">
                              <Label className="text-xs">Date d'encaissement programmée</Label>
                              <DatePickerFr
                                value={partialPaymentData.scheduled_date || ''}
                                onChange={(v) => setPartialPaymentData({...partialPaymentData, scheduled_date: v})}
                                minToday
                                className="mt-1 w-full"
                              />
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Montant (€) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={partialPaymentsHistory.reste_a_payer || 0}
                            value={partialPaymentData.montant}
                            onChange={(e) => setPartialPaymentData({...partialPaymentData, montant: e.target.value})}
                            placeholder={`Max: ${(partialPaymentsHistory.reste_a_payer || 0).toFixed(2)} €`}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Date du paiement *</Label>
                          <DatePickerFr
                            value={partialPaymentData.date_paiement}
                            onChange={(v) => setPartialPaymentData({...partialPaymentData, date_paiement: v})}
                            className="mt-1 w-full"
                          />
                        </div>
                        <div>
                          <Label>Mode de paiement</Label>
                          <Select
                            value={partialPaymentData.mode_paiement}
                            onValueChange={(value) => setPartialPaymentData({...partialPaymentData, mode_paiement: value})}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="virement">Virement bancaire</SelectItem>
                              <SelectItem value="sepa_core">Prélèvement SEPA Core</SelectItem>
                              <SelectItem value="sepa_b2b">Prélèvement SEPA B2B</SelectItem>
                              <SelectItem value="carte">Carte bancaire</SelectItem>
                              <SelectItem value="cheque">Chèque</SelectItem>
                              <SelectItem value="especes">Espèces</SelectItem>
                              <SelectItem value="autre">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Référence</Label>
                          <Input
                            value={partialPaymentData.reference}
                            onChange={(e) => setPartialPaymentData({...partialPaymentData, reference: e.target.value})}
                            placeholder="N° de virement, chèque..."
                            className="mt-1"
                          />
                        </div>
                      </div>

                      {/* Champs SEPA additionnels */}
                      {(partialPaymentData.mode_paiement === 'sepa_core' || partialPaymentData.mode_paiement === 'sepa_b2b') && (
                        <div className="mt-3 bg-indigo-50 border border-indigo-200 p-3 rounded-lg space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-indigo-800">
                              {partialPaymentData.mode_paiement === 'sepa_b2b' ? 'SEPA B2B (Entreprise)' : 'SEPA Core (Particulier)'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-indigo-600">Nom du titulaire</Label>
                              <Input
                                data-testid="ess-sepa-customer-name"
                                value={partialPaymentData.sepa_customer_name || ''}
                                onChange={(e) => setPartialPaymentData({...partialPaymentData, sepa_customer_name: e.target.value})}
                                placeholder="Nom complet"
                                className="mt-1 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-indigo-600">Email du client</Label>
                              <Input
                                data-testid="ess-sepa-customer-email"
                                type="email"
                                value={partialPaymentData.sepa_customer_email || ''}
                                onChange={(e) => setPartialPaymentData({...partialPaymentData, sepa_customer_email: e.target.value})}
                                placeholder="email@exemple.com"
                                className="mt-1 text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-indigo-600">IBAN</Label>
                            <Input
                              data-testid="ess-sepa-iban"
                              value={partialPaymentData.sepa_iban || ''}
                              onChange={(e) => setPartialPaymentData({...partialPaymentData, sepa_iban: e.target.value.toUpperCase()})}
                              placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                              className="mt-1 text-sm font-mono"
                            />
                          </div>
                          <Button
                            type="button"
                            data-testid="ess-sepa-stripe-btn"
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                            disabled={processingPartialPayment || !partialPaymentData.sepa_customer_name || !partialPaymentData.sepa_customer_email || !partialPaymentData.sepa_iban}
                            onClick={async () => {
                              const doc = selectedDocument;
                              if (!doc) return;
                              try {
                                await axios.post(
                                  `${API}/ess/${doc.id || doc.numero_police}/setup-sepa`,
                                  {
                                    customer_email: partialPaymentData.sepa_customer_email,
                                    customer_name: partialPaymentData.sepa_customer_name,
                                    iban: partialPaymentData.sepa_iban,
                                    sepa_type: partialPaymentData.mode_paiement === 'sepa_b2b' ? 'b2b' : 'core',
                                    montant: parseFloat(partialPaymentData.montant) || 0
                                  },
                                  { headers: getAuthHeader() }
                                );
                                toast.success(`Prélèvement ${partialPaymentData.mode_paiement === 'sepa_b2b' ? 'SEPA B2B' : 'SEPA Core'} lancé via Stripe`);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erreur SEPA Stripe');
                              }
                            }}
                          >
                            Lancer le prélèvement SEPA via Stripe
                          </Button>
                          <p className="text-[10px] text-indigo-500">
                            {partialPaymentData.mode_paiement === 'sepa_b2b'
                              ? 'SEPA B2B : Délai J-1, non remboursable — entreprises avec mandat B2B.'
                              : 'SEPA Core : Délai J-14, remboursable 8 semaines — particuliers et TPE.'}
                          </p>
                        </div>
                      )}
                      
                      <div className="mt-4">
                        <Label>Observations</Label>
                        <Textarea
                          value={partialPaymentData.observations}
                          onChange={(e) => setPartialPaymentData({...partialPaymentData, observations: e.target.value})}
                          placeholder="Notes ou commentaires..."
                          className="mt-1"
                          rows={2}
                        />
                      </div>

                      {/* Lien de paiement Stripe */}
                      <div className="mt-3">
                        <PaymentLinkSection
                          reference={selectedDocument?.numero_police || selectedDocument?.id || ''}
                          amount={parseFloat(partialPaymentData.montant) || (partialPaymentsHistory?.reste_a_payer || 0)}
                          customerEmail={selectedDocument?.email || selectedDocument?.membre_email || selectedDocument?.member?.email || ''}
                          customerName={selectedDocument?.raison_sociale || selectedDocument?.nom || selectedDocument?.member?.nom || ''}
                          source="ess"
                          description={`Contribution ESS - ${selectedDocument?.numero_police || ''}`}
                          authHeader={getAuthHeader()}
                        />
                      </div>
                    </div>
                  )}

                  {/* Message si soldé */}
                  {(partialPaymentsHistory.reste_a_payer || 0) <= 0.01 && partialPaymentsHistory.paiements?.length > 0 && (
                    <Alert className="bg-green-50 border-green-500">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-700">
                        <strong>Paiement soldé !</strong> Cette souscription a été entièrement payée.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPartialPaymentModal(false)}
            >
              Fermer
            </Button>
            {(partialPaymentsHistory.reste_a_payer || 0) > 0.01 && (
              <Button
                onClick={handlePartialPayment}
                disabled={processingPartialPayment || !partialPaymentData.montant}
                className={partialPaymentData.type_paiement === 'acompte' 
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-orange-600 hover:bg-orange-700 text-white"
                }
              >
                {processingPartialPayment ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : partialPaymentData.type_paiement === 'acompte' ? (
                  <>
                    <Wallet className="h-4 w-4 mr-2" />
                    {partialPaymentData.is_scheduled ? 'Programmer l\'acompte' : 'Enregistrer l\'acompte'}
                  </>
                ) : (
                  <>
                    <Banknote className="h-4 w-4 mr-2" />
                    Enregistrer le paiement
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de modification d'échéance */}
    </>
  );
}
